import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  CheckoutDetails as CheckoutDetailsReusable,
  COUNTRIES,
  FormData,
  CheckoutDetailsTranslations,
  useToBreakpoint,
} from '@cdaas/reusable-components-daas';
import { Delivery } from '@cdaas/reusable-components-daas/dist/src/asset-api-types/api-types';
import { LocalBundleEnvelopeWithChosenMainVariant } from '@cdaas/reusable-components-daas/dist/src/product-api-types/api-types';
import { InvoicingAccountSelectionTranslations } from '@cdaas/reusable-components-daas/dist/src/components/InvoicingAccountSelection/types';
import { GridBag, Gutter, Loader, Page, Headline } from '@gaiads/telia-react-component-library';
import { InvoicingAccountOptionResponseContents, OrganizationResponse, UserResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';

import { AppDispatch } from '../../../store';
import { getStorageItems, ORDER_FLOW_CONSTANTS, typeGuards, parsingHelpers } from '../../../utils';
import {
  resetLocationReference,
  selectDeliveryDetails,
  selectOrganization,
  selectSelectedUser,
  selectSelectedProduct,
  setOrderDeliveryDetails,
  setOrderProduct,
} from '../../../store/order';
import { assetAdminInvoicingAccountClient } from '../../../api-client/asset-admin-client';
import ProductCart from '../ProductCart/ProductCart';
import { MOBILE_BREAKPOINT } from '../Order';

export default function CheckoutDetails() {
  const organization: OrganizationResponse | null = useSelector(selectOrganization);
  const selectedUserStore: UserResponse | null = useSelector(selectSelectedUser);
  const selectedOrganizationStore: OrganizationResponse | null = useSelector(selectOrganization);
  const deliveryDetailsStore: Delivery | null = useSelector(selectDeliveryDetails);
  const selectedProduct: LocalBundleEnvelopeWithChosenMainVariant | null = useSelector(selectSelectedProduct);

  const isMobile = useToBreakpoint(MOBILE_BREAKPOINT);

  const [deliveryDetails, setDeliveryDetails] = useState<Delivery>();
  const [organizationInvoicingAccounts, setOrganizationInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const { t } = useTranslation('orderFlow');
  const { t: tGlobal } = useTranslation('global');

  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();

  const initDeliveryDetailsFields = (details: UserResponse | OrganizationResponse | Delivery) => {
    if (typeGuards.isUser(details)) {
      const parsedNumber = parsingHelpers.extractPartsFromFullPhoneNumber(
        Object.hasOwn(details, 'phone_number') ? (details as UserResponse).phone_number : undefined,
      );

      setDeliveryDetails({
        deliveryContact: {
          first_name: details.given_name ?? '',
          last_name: details.family_name ?? '',
          email: details.email ?? '',
          phone_number: parsedNumber?.nationalNumber.toString() ?? '',
          country_calling_code: parsedNumber?.countryCallingCode.toString() ?? '',
          title: '',
        },
        deliveryAddress: {
          name: '',
          address1: '',
          address2: '',
          street: '',
          city: '',
          zip: '',
          country: '',
        },
        isHomeDelivery: false,
        locationReference: '',
        invoicingAccountNumber: '',
      });
    } else if (typeGuards.isOrganization(details)) {
      setDeliveryDetails({
        deliveryContact: {
          first_name: '',
          last_name: '',
          email: '',
          phone_number: '',
          country_calling_code: '',
          title: '',
        },
        deliveryAddress: {
          name: '',
          address1: '',
          address2: '',
          street: '',
          city: '',
          zip: '',
          country: details.default_billing_address.country ?? '',
        },
        isHomeDelivery: false,
        locationReference: '',
        invoicingAccountNumber: '',
      });
    } else {
      setDeliveryDetails(details);
    }
  };

  useEffect(() => {
    const [selectedProductInSessionStorage] = getStorageItems(['tItem'], 'session', [true]);

    if (
      !selectedProduct &&
      selectedProductInSessionStorage &&
      typeGuards.isLocalBundleEnvelopeWithChosenMainVariant(selectedProductInSessionStorage)
    ) {
      dispatch(setOrderProduct(selectedProductInSessionStorage));
    } else if (!selectedUserStore && !selectedOrganizationStore) {
      history.push('/');
    } else if (!selectedProduct) {
      history.push(ORDER_FLOW_CONSTANTS.selectProduct);
    }
  }, [dispatch, selectedProduct]);

  useEffect(() => {
    if (selectedUserStore && !deliveryDetailsStore) {
      initDeliveryDetailsFields(selectedUserStore);
    } else if (selectedUserStore && deliveryDetailsStore) {
      initDeliveryDetailsFields(deliveryDetailsStore);
    } else if (!selectedUserStore && selectedOrganizationStore && !deliveryDetailsStore) {
      initDeliveryDetailsFields(selectedOrganizationStore);
    } else if (!selectedUserStore && selectedOrganizationStore && deliveryDetailsStore) {
      initDeliveryDetailsFields(deliveryDetailsStore);
    }
  }, [selectedUserStore, selectedOrganizationStore, deliveryDetailsStore]);

  useEffect(() => {
    const fetchData = async () => {
      if (organization) {
        try {
          const invoicingAccounts = await assetAdminInvoicingAccountClient.adminReadInvoicingAccountOptions(organization.id, 99999);
          setOrganizationInvoicingAccounts(invoicingAccounts.data.contents);
        } catch (error) {
          console.error('Invoice account list reading failed', error);
          setOrganizationInvoicingAccounts([]);
        }
      }
      setLoading(false);
    };

    fetchData();
  }, [organization]);

  const handleNextClick = (data: FormData) => {
    history.push(ORDER_FLOW_CONSTANTS.deliveryOptions);
  };

  const handleBackClick = () => {
    dispatch(resetLocationReference());
    history.push(ORDER_FLOW_CONSTANTS.selectProduct);
  };

  const handleDataSaving = (data: FormData) => {
    const deliveryDetails: Delivery = {
      deliveryAddress: {
        name: '',
        address1: data.address1,
        address2: data.address2,
        street: data.street,
        city: data.city,
        zip: data.zip,
        country: data.country,
      },
      deliveryContact: {
        title: '',
        first_name: data.first_name,
        last_name: data.last_name,
        phone_number: data.phone_number,
        country_calling_code: data.country_calling_code,
        email: data.email,
      },
      isHomeDelivery: data.homeDelivery,
      locationReference: data.location_reference.trim() === '' ? null : data.location_reference,
      invoicingAccountNumber: data.invoicing_account_number.trim() === '' ? null : data.invoicing_account_number,
    };
    dispatch(setOrderDeliveryDetails(deliveryDetails));
  };

  const checkoutDetailsTranslations: CheckoutDetailsTranslations = {
    companyName: t('checkoutDetails.companyName'),
    employeeTitle: t('checkoutDetails.employeeTitle'),
    billingTitle: t('checkoutDetails.billingTitle'),
    deliveryTitle: t('checkoutDetails.deliveryTitle'),
    billingTextOne: t('checkoutDetails.billingTextOne'),
    billingTextTwo: t('checkoutDetails.billingTextTwo'),
    deliveryText: t('checkoutDetails.deliveryText'),
    deliveryQuestion: t('checkoutDetails.deliveryQuestion'),
    firstName: t('checkoutDetails.form.firstName'),
    lastName: t('checkoutDetails.form.lastName'),
    email: t('checkoutDetails.form.email'),
    phoneNbr: t('checkoutDetails.form.phoneNbr'),
    address1: t('checkoutDetails.form.address1'),
    address2: t('checkoutDetails.form.address2'),
    streetAddress: t('checkoutDetails.form.streetAddress'),
    postalCode: t('checkoutDetails.form.postalCode'),
    city: t('checkoutDetails.form.city'),
    country: t('checkoutDetails.form.country'),
    countryPlaceholder: t('checkoutDetails.form.countryPlaceholder'),
    companyDeliveryOption: t('checkoutDetails.form.companyDeliveryOption'),
    companyDeliveryOptionDetails: t('checkoutDetails.form.companyDeliveryOptionDetails'),
    homeDeliveryOption: t('checkoutDetails.form.homeDeliveryOption'),
    homeDeliveryOptionDetails: t('checkoutDetails.form.homeDeliveryOptionDetails'),
    location: t('checkoutDetails.form.location'),
    locationReference: t('checkoutDetails.form.locationReference'),
    locationReferenceHint: t('checkoutDetails.form.locationReferenceHint'),
    invoicingAccountTitle: t('checkoutDetails.form.invoicingAccount.title'),
    required: t('checkoutDetails.form.rules.required'),
    invalidEmail: t('checkoutDetails.form.rules.invalidEmail'),
    invalidPhoneNumber: t('checkoutDetails.form.rules.invalidPhoneNumber'),
    invalidPostalCode: t('checkoutDetails.form.rules.invalidPostalCode'),
    nextButton: t('checkoutDetails.nextButton'),
    cancelButton: t('checkoutDetails.cancelButton'),
    Finland: tGlobal('countries.Finland'),
    Sweden: tGlobal('countries.Sweden'),
    Norway: tGlobal('countries.Norway'),
    Denmark: tGlobal('countries.Denmark'),
    UnitedKingdom: tGlobal('countries.UnitedKingdom'),
  };

  const invoicingAccountTranslations: InvoicingAccountSelectionTranslations = {
    dropdownLabel: t('checkoutDetails.form.invoicingAccount.dropdownLabel'),
    clearSelection: t('checkoutDetails.form.invoicingAccount.clearSelection'),
    helperText: t('checkoutDetails.form.invoicingAccount.helperText'),
    noOptionsFoundInfo: t('checkoutDetails.form.invoicingAccount.noOptionsFoundInfo'),
  };

  if (loading) {
    return (
      <Page>
        <Page.Row alignTextToCenter margin={{ vertical: 'xxlg' }}>
          <Loader />
        </Page.Row>
      </Page>
    );
  }

  return (
    <>
      <Gutter size={'layout-5'} />
      <Headline singleLine size={'lg'}>
        {t('checkoutDetails.headline')}
      </Headline>
      <Gutter size={'layout-5'} />

      <GridBag showGridLines noGutter>
        <GridBag.Item md={12} lg={6}>
          <CheckoutDetailsReusable
            companyName={organization?.name || ''}
            countryCodes={COUNTRIES}
            handleBackClick={handleBackClick}
            handleNextClick={handleNextClick}
            handleSavingDataOnBlurSubmitRestore={handleDataSaving}
            showLocationReference={true}
            showInvoicingAccount={true}
            showSelectedUser={!!selectedUserStore}
            selectedUser={selectedUserStore ?? undefined}
            translations={checkoutDetailsTranslations}
            invoicingAccountTranslations={invoicingAccountTranslations}
            inheritedInvoicingAccount={
              selectedUserStore?.invoicing_account?.name ||
              selectedUserStore?.inherited_invoicing_account.name ||
              selectedOrganizationStore?.call_off_number ||
              ''
            }
            invoicingAccounts={organizationInvoicingAccounts}
            deliveryDetails={deliveryDetails}
            ProductCart={isMobile ? <ProductCart isMobile /> : undefined}
          />
        </GridBag.Item>
        {!isMobile ? (
          <GridBag.Item lg={6} className={'order-product-cart-container'}>
            <GridBag>
              <GridBag.Item lg={4}></GridBag.Item>
              <GridBag.Item lg={8} className={'order-product-cart-border'}>
                <ProductCart />
              </GridBag.Item>
            </GridBag>
          </GridBag.Item>
        ) : undefined}
      </GridBag>
    </>
  );
}
